/**
 * Keeper eligibility and cost.
 *
 * This is the heart of the league. A keeper declaration has to answer three questions:
 *   1. Is this player eligible to be kept at all?
 *   2. What draft pick does it cost?
 *   3. What is he worth next year if I keep him again?
 *
 * Rules in play, in the order they were passed:
 *   2012-III-1  "The Motyka Rule"          — original +2+1 system (superseded by the Grid)
 *   2017-II-1   "The Grid Rule" + escalation
 *   2017-II-2   sleeper reward (Round 10+, never dropped)
 *   2018-III-2  eliminated the old "3 players for 3 years" cap
 *   2019-II-1   the current Grid
 *   2022        UDFA valued at (last round + 1); sleeper void if he was hurt when drafted
 *   2023        max 8 keepers with a FAAB luxury tax
 *   2026        commissioner ruling: a drop never lowers a player's value, only costs the
 *               sleeper reward. A trade does not reset anything.
 *
 * NOT implemented, deliberately: 2013-III-3 / 2015-III-8 / 2016-III-1, the "keeper eligibility
 * reset" rules. They reset a three-year clock that 2018-III-2 abolished, and apply a "+2"
 * penalty that the Grid replaced. See OPEN-QUESTIONS.md #1.
 */

import type { LeagueConfig } from './config.js';
import {
  NOT_ELIGIBLE,
  escalationChain,
  farmGraduationRound,
  maxKeeperYears,
  penaltyRound,
  udfaRoundValue,
} from './grid.js';

export type Acquisition =
  /** Selected in the main fantasy draft. */
  | 'drafted'
  /** Picked up off waivers or free agency; undrafted in the most recent fantasy draft. */
  | 'udfa'
  /** Won in the farm-system auction and has since entered the NFL. */
  | 'farm'
  /** Acquired by trade from another GM. */
  | 'trade';

export interface KeeperRecord {
  playerId: string;
  teamId: string;
  /** Season this record describes — i.e. the season just completed. */
  season: number;
  acquisition: Acquisition;
  /**
   * The round value the Grid reads to price the NEXT keeper year. For a player in his first
   * year this is his actual draft round; after each keeper year it becomes the round he cost.
   */
  currentRoundValue: number;
  /** Consecutive keeper seasons already served by this GM. 0 for a player never yet kept. */
  yearsKept: number;
  /** Round he was originally drafted in, for display/audit. Null for UDFA. */
  originalRound: number | null;
  /** True once the one-time sleeper waiver has been consumed. */
  sleeperUsed: boolean;
  /** Was he on the roster for the whole regular season without being dropped? (2017-II-2 §2) */
  neverDropped: boolean;
  /** Was he on season-ending IR entering the next season? Voids the sleeper waiver. (2017-II-2 §5) */
  onSeasonEndingIR: boolean;
  /**
   * Was he ALREADY on NFL injured reserve when he was drafted — i.e. at the start of the
   * fantasy draft in which this GM selected him (the 2025 draft, for 2026 keeper purposes)?
   *
   * 2022 "Amendments To Sleeper Reward Provision" §1. The sleeper reward exists to repay a GM
   * for spotting a player everyone else overlooked. A player who fell to Round 12 because he
   * was visibly injured on draft day was not overlooked — he was priced. So the discount is
   * withheld and he keeps his ordinary first-year penalty.
   *
   * §2 draws the line at the draft itself: an injury AFTER the draft does not cost the reward.
   * This is a different rule from onSeasonEndingIR, which is 2017-II-2 §5 and looks at his
   * status entering the FOLLOWING season.
   */
  onIRBeforeDraft: boolean;
  /** Farm graduate: the NFL draft round he was taken in, or null if he went undrafted. */
  nflDraftRound?: number | null;
  /** Farm graduate who failed to make any opening-day 53-man roster. (2019-II-5) */
  farmFailedToMakeRoster?: boolean;
  /**
   * The GM is applying his one franchise tag to this player.
   * 2025 Proposal 05 (Amit) — PASSED. Waives the Grid penalty for one season; in exchange the
   * player consumes THREE keeper slots.
   */
  franchiseTagged?: boolean;
  /** He has been franchise-tagged before and may not be tagged again (2025 P05 §3). */
  previouslyFranchised?: boolean;
  /**
   * He entered the league through the farm auction at some point, even if he has since become
   * an ordinary keeper. 2025 P05 §4 excludes players "originally acquired via the Farm
   * System", which the commissioner reads as a lifetime bar: he refused Luke's attempt to
   * franchise Jaxon Smith-Njigba in 2025 — "you can't franchise JSN. Farm system guy." — two
   * years after JSN graduated and stopped being a farm player in any other sense.
   */
  farmOrigin?: boolean;
}

/**
 * Is this player eligible to receive the franchise tag?
 *
 * 2025 Proposal 05 §3: "Eligible players must have been selected in rounds 3-9 of the
 *   preceding draft, and not previously been held under the franchise tag."
 * §4: farm-system, Sleeper Reward and UDFA players are ineligible — "these players have
 *   already been retained at a discounted rate."
 * §5: players kept from prior seasons are eligible provided they meet the criteria above.
 *
 * Note this is Amit's 2025 rule. A DIFFERENT franchise tag (Vishal's 2024 Proposal 03, which
 * priced the tag by position and applied to unkeepable 1st/2nd-round picks) FAILED. Round 1
 * and Round 2 picks remain unkeepable.
 */
export function franchiseTagEligible(
  record: KeeperRecord,
): { eligible: boolean; reason: string } {
  if (record.previouslyFranchised) {
    return {
      eligible: false,
      reason: 'Already held under the franchise tag once; a player cannot be tagged twice (2025 P05 §3).',
    };
  }
  if (record.acquisition === 'farm' || record.farmOrigin) {
    return {
      eligible: false,
      reason: 'Originally acquired through the farm system, which bars him for good — ' +
        'he was already retained at a discount once (2025 P05 §4).',
    };
  }
  if (record.acquisition === 'udfa') {
    return { eligible: false, reason: 'Undrafted free agents are ineligible (2025 P05 §4).' };
  }
  if (record.sleeperUsed) {
    return {
      eligible: false,
      reason: 'Already retained at a discount via the Sleeper Reward provision (2025 P05 §4).',
    };
  }
  const r = record.currentRoundValue;
  if (r < 3 || r > 9) {
    return {
      eligible: false,
      reason: `Round ${r} value is outside the eligible band of rounds 3-9 (2025 P05 §3).`,
    };
  }
  return {
    eligible: true,
    reason:
      `Round ${r} value, never franchised, not a farm/sleeper/UDFA acquisition — eligible ` +
      'for the franchise tag (2025 P05 §3).',
  };
}

export interface KeeperCost {
  eligible: boolean;
  /** The draft round forfeited to keep him, or null if he cannot be kept. */
  costRound: number | null;
  /** True when the sleeper reward waives the penalty this year. */
  isSleeperYear: boolean;
  /** Keeper years still available after this one, per the Grid's Max Keeper Length. */
  yearsRemainingAfter: number;
  /** The round value he will carry into next season if kept this year. */
  nextRoundValue: number | null;
  /** Human-readable explanation, with rule citations. Always populated. */
  explanation: string;
  /** Rule IDs that determined this outcome. */
  citations: string[];
}

/**
 * Does the sleeper reward apply to this player's first keeper year?
 *
 * 2017-II-2 §2: waived if drafted in Round 10 or later AND never dropped during the regular
 *   season.
 * 2017-II-2 §4: farm-system players are excluded.
 * 2017-II-2 §5: void if he enters the next season on season-ending IR.
 * 2022 amendment §1: void if he was already on NFL IR at or before that year's fantasy draft.
 * 2017-II-2 §3: a player drafted R10+ by GM A, traded to GM B, and held by B for the rest of
 *   that season earns B the same reward. Callers should pass `acquisition: 'trade'` with the
 *   ORIGINAL draft round in `currentRoundValue` for that case.
 */
export function sleeperApplies(record: KeeperRecord, config: LeagueConfig): boolean {
  if (record.sleeperUsed) return false;
  if (record.yearsKept > 0) return false; // first keeper year only
  if (record.acquisition === 'farm') return false; // §4
  if (record.acquisition === 'udfa') return false; // never "drafted in Round 10 or later"
  if (record.currentRoundValue < config.sleeperMinRound) return false; // §2
  if (!record.neverDropped) return false; // §2
  if (record.onSeasonEndingIR) return false; // §5
  if (record.onIRBeforeDraft) return false; // 2022
  return true;
}

/**
 * The round value the Grid should read for this player, resolving UDFA and farm graduates to
 * their equivalent draft round.
 */
export function effectiveRoundValue(record: KeeperRecord, config: LeagueConfig): number {
  if (record.acquisition === 'udfa' && record.yearsKept === 0) {
    return udfaRoundValue(config); // 2022
  }
  if (record.acquisition === 'farm' && record.yearsKept === 0) {
    return farmGraduationRound(record.nflDraftRound ?? null, config); // 2013-III-1 §4, 2018-II-1
  }
  return record.currentRoundValue;
}

/**
 * Price a keeper declaration.
 */
export function keeperCost(record: KeeperRecord, config: LeagueConfig): KeeperCost {
  const citations: string[] = [];

  // A farm player who never made an NFL roster cannot be kept, and per 2019-II-5 §1 the GM
  // forfeits the keeper slot without being allowed to substitute another player into it.
  // That slot-forfeiture is applied in luxuryTax.ts; here we just refuse the declaration.
  if (record.acquisition === 'farm' && record.farmFailedToMakeRoster) {
    return {
      eligible: false,
      costRound: null,
      isSleeperYear: false,
      yearsRemainingAfter: 0,
      nextRoundValue: null,
      explanation:
        'Farm-system player failed to make an opening-day 53-man roster. He cannot be kept, ' +
        'and the GM forfeits one of his free keeper slots without being allowed to name a ' +
        'replacement (2019-II-5 §1).',
      citations: ['2019-II-5'],
    };
  }

  // A farm graduate's rookie keeper year is priced DIRECTLY at his graduation round — the
  // "+10" in 2013-III-1 §4 is the penalty itself, not a value the Grid then escalates. A 1st-
  // round NFL pick costs an 11th-round fantasy pick, full stop.
  //
  // Verified against two seasons of the league's own keeper sheets: Brock Bowers graduated in
  // 2024 at a cost of Round 11 (NFL 1.13), then cost Round 7 in 2025 — i.e. the year after
  // graduation he is an ordinary Round 11 value walking the Grid. Marvin Harrison Jr. shows
  // the identical 11 -> 7 pattern.
  //
  // Per 2015-III-5, the graduation year is on top of the normal run: he then gets the full
  // keeper length the Grid gives his graduation round.
  if (record.acquisition === 'farm' && record.yearsKept === 0) {
    const gradRound = farmGraduationRound(record.nflDraftRound ?? null, config);
    citations.push('2013-III-1', '2018-II-1', '2015-III-5');
    return {
      eligible: true,
      costRound: gradRound,
      isSleeperYear: false,
      yearsRemainingAfter: maxKeeperYears(gradRound),
      nextRoundValue: gradRound,
      explanation:
        `Farm-system graduate. NFL ${
          record.nflDraftRound === null || record.nflDraftRound === undefined
            ? 'undrafted but made a 53-man roster'
            : `Round ${record.nflDraftRound}`
        } + 10 = a Round ${gradRound} pick, capped at the last round of the draft ` +
        '(2013-III-1 §4, 2018-II-1). Mandatory keeper for his rookie season, and he occupies ' +
        `one keeper slot. From next season he is an ordinary Round ${gradRound} value with ` +
        `${maxKeeperYears(gradRound)} keeper year(s) ahead of him (2015-III-5).`,
      citations,
    };
  }

  let value = effectiveRoundValue(record, config);
  if (record.acquisition === 'udfa' && record.yearsKept === 0) citations.push('2022-UDFA-VALUE');

  // THE DROP RULE. Commissioner ruling, 2026, and the operative reading of the tangle of
  // reset rules (2013-III-3 / 2015-III-8 / 2016-III-1) under the Grid:
  //
  //   "If a player is dropped at all, they turn into UDFA value or retain their previous
  //    value — whichever is more costly — but lose benefits like the sleeper reward."
  //
  // "More costly" means the EARLIER round, i.e. the lower round number: forfeiting a 5th is
  // dearer than forfeiting a 12th. So the dropped value is min(previous, UDFA).
  //
  // The point of the rule is anti-abuse. Without it, a GM could drop a Round 3 stud, re-add
  // him off waivers, and launder him into a Round 12 UDFA. With it, a drop can never make a
  // player cheaper — it can only cost him the sleeper reward. Since UDFA value is one round
  // past the end of the draft, in practice every drafted player simply keeps his own value.
  //
  // This supersedes the speculative "+2 on reset" reading previously carried here.
  if (!record.neverDropped) {
    const udfa = udfaRoundValue(config);
    const before = value;
    value = Math.min(value || udfa, udfa);
    citations.push('2026-DROP-RULE');
    if (value !== before) {
      // Only bites for a player with no prior draft value of his own.
      citations.push('2022-UDFA-VALUE');
    }
  }

  // Franchise tag (2025 P05): the Grid penalty is waived entirely for one season. He is kept
  // at his current round value, and "the player's keeper escalation cost will resume the
  // following season" (§2) — so his value does NOT advance this year.
  // The price is paid in slots, not picks: he occupies three of them. That is applied in
  // luxuryTax.ts, which is where slot accounting lives.
  if (record.franchiseTagged) {
    const check = franchiseTagEligible(record);
    if (!check.eligible) {
      return {
        eligible: false,
        costRound: null,
        isSleeperYear: false,
        yearsRemainingAfter: 0,
        nextRoundValue: null,
        explanation: `Franchise tag rejected: ${check.reason}`,
        citations: ['2025-proposal-05-franchise-tag'],
      };
    }
    citations.push('2025-proposal-05-franchise-tag');
    return {
      eligible: true,
      costRound: value,
      isSleeperYear: false,
      yearsRemainingAfter: maxKeeperYears(value),
      nextRoundValue: value,
      explanation:
        `Franchise tagged. The Grid penalty is waived: he is kept at his Round ${value} value ` +
        `instead of escalating to Round ${penaltyRound(value)} (2025 P05 §1). He consumes ` +
        'THREE keeper slots (§2), and normal escalation resumes next season.',
      citations,
    };
  }

  const sleeper = sleeperApplies(record, config);

  if (sleeper) {
    // 2017-II-2 §2 waives the PENALTY ROUND, not the pick. He still costs a pick — he costs
    // the round he already occupies, rather than escalating up the Grid. The league's own
    // keeper sheets record it exactly this way: Jameson Williams "13*" against a 2024 round
    // of 13, Joe Burrow "12*" against 12, Bucky Irving "14*" against 14.
    citations.push('2017-II-2');
    return {
      eligible: true,
      costRound: value,
      isSleeperYear: true,
      yearsRemainingAfter: maxKeeperYears(value),
      nextRoundValue: value,
      explanation:
        `Sleeper reward: drafted in Round ${value} (Round ${config.sleeperMinRound} or later) ` +
        `and never dropped, so the Penalty Round is waived (2017-II-2 §2). He costs a Round ` +
        `${value} pick — the same round he already occupies — rather than escalating to Round ` +
        `${penaltyRound(value)}. He carries that Round ${value} value into next season, when ` +
        'normal escalation resumes.',
      citations,
    };
  }

  const cost = penaltyRound(value);
  citations.push('2019-II-1');

  if (cost === NOT_ELIGIBLE) {
    return {
      eligible: false,
      costRound: null,
      isSleeperYear: false,
      yearsRemainingAfter: 0,
      nextRoundValue: null,
      explanation:
        `Round ${value} value is Not Eligible on the Grid — there is no earlier pick to ` +
        'forfeit (2019-II-1). His keeper run is over.',
      citations,
    };
  }

  return {
    eligible: true,
    costRound: cost,
    isSleeperYear: false,
    yearsRemainingAfter: maxKeeperYears(cost),
    nextRoundValue: cost,
    explanation:
      `Round ${value} value costs a Round ${cost} pick to keep (2019-II-1). ` +
      `After this season he is a Round ${cost} value with ${maxKeeperYears(cost)} keeper ` +
      'year(s) left.',
    citations,
  };
}

/**
 * Produce next season's record for a player who was kept this season.
 * `outcome` carries the facts only the season itself can supply.
 */
export function advanceKeeper(
  record: KeeperRecord,
  config: LeagueConfig,
  outcome: {
    neverDropped: boolean;
    onSeasonEndingIR: boolean;
    onIRBeforeDraft: boolean;
    /** Set if he changed hands this offseason and the receiving GM accepted the reset. */
    /** Set if he was traded; changes the owning team. */
    newTeamId?: string;
  },
): KeeperRecord {
  const cost = keeperCost(record, config);
  if (!cost.eligible || cost.nextRoundValue === null) {
    throw new Error(
      `Cannot advance ${record.playerId}: he was not eligible to be kept. ${cost.explanation}`,
    );
  }
  return {
    playerId: record.playerId,
    teamId: outcome.newTeamId ?? record.teamId,
    season: record.season + 1,
    // After the first keeper year he is priced off his round value like any drafted player.
    acquisition: outcome.newTeamId ? 'trade' : 'drafted',
    currentRoundValue: cost.nextRoundValue,
    yearsKept: record.yearsKept + 1,
    originalRound: record.originalRound,
    sleeperUsed: record.sleeperUsed || cost.isSleeperYear,
    neverDropped: outcome.neverDropped,
    onSeasonEndingIR: outcome.onSeasonEndingIR,
    onIRBeforeDraft: outcome.onIRBeforeDraft,
  };
}

/**
 * The full remaining cost schedule for a player if his GM keeps him every year until he runs
 * out of eligibility. Useful for the "should I keep him?" view on the draft board.
 */
export function keeperProjection(
  record: KeeperRecord,
  config: LeagueConfig,
): Array<{ season: number; costRound: number | null; isSleeperYear: boolean }> {
  const out: Array<{ season: number; costRound: number | null; isSleeperYear: boolean }> = [];
  const first = keeperCost(record, config);
  if (!first.eligible) return out;

  out.push({
    season: record.season + 1,
    costRound: first.costRound,
    isSleeperYear: first.isSleeperYear,
  });

  // After the first year, escalation is deterministic from the value he carries forward.
  const chain = escalationChain(first.nextRoundValue!);
  chain.forEach((costRound, i) => {
    out.push({ season: record.season + 2 + i, costRound, isSleeperYear: false });
  });
  return out;
}

/** Convenience: build a fresh record for a player just selected in the fantasy draft. */
export function newDraftedPlayer(
  playerId: string,
  teamId: string,
  season: number,
  round: number,
): KeeperRecord {
  return {
    playerId,
    teamId,
    season,
    acquisition: 'drafted',
    currentRoundValue: round,
    yearsKept: 0,
    originalRound: round,
    sleeperUsed: false,
    neverDropped: true,
    onSeasonEndingIR: false,
    onIRBeforeDraft: false,
  };
}
